import type { AirportGroup } from '../ui/airportGroups';
import { airportById } from '../data/airports';
import { distanceKm } from '../simulation/math';
import { useI18n } from '../i18n';

interface MobileAirportListProps {
  groups: AirportGroup[];
  listMode: boolean;
  selectedAirportId?: string;
  routeOriginId?: string;
  reachableAirportIds?: ReadonlySet<string>;
  routeAirportIds?: ReadonlySet<string>;
  routeAirportCounts?: ReadonlyMap<string, number>;
  hubAirportIds?: ReadonlySet<string>;
  onAirportSelect: (iata: string) => void;
  onToggleListMode: () => void;
}

export function MobileAirportList({ groups, listMode, selectedAirportId, routeOriginId, reachableAirportIds, routeAirportIds, routeAirportCounts, hubAirportIds, onAirportSelect, onToggleListMode }: MobileAirportListProps) {
  const { t } = useI18n();
  const planningMode = Boolean(routeOriginId);
  const distanceReferenceId = routeOriginId ?? selectedAirportId;
  const distanceReference = distanceReferenceId ? airportById(distanceReferenceId) : undefined;

  function marketClass(market: number) {
    if (market >= 9) return 'market-orange';
    if (market === 8) return 'market-blue';
    if (market > 6) return 'market-green';
    return '';
  }

  return (
    <section className={`mobile-airport-list-shell ${listMode ? 'list-mode' : 'map-mode'}`} aria-label={t('ui.机场浏览')}>
      <header className="mobile-airport-list-toolbar">
        <div>
          <span>{t('ui.机场浏览')}</span>
          {listMode && <strong>{t('ui.共 {count} 个机场', { count: groups.reduce((total, group) => total + group.airports.length, 0) })}</strong>}
        </div>
        <button type="button" onClick={onToggleListMode} aria-pressed={listMode}>
          {listMode ? t('ui.切换地图') : t('ui.切换机场列表')}
        </button>
      </header>
      {listMode && (
        <div className="mobile-airport-groups">
          {groups.map((group) => (
            <section className="mobile-airport-group" key={group.label} aria-labelledby={`airport-group-${group.label}`}>
              <div className="mobile-airport-group-heading">
                <h2 id={`airport-group-${group.label}`}>{group.label}</h2>
                <span>{group.airports.length}</span>
              </div>
              <div className="mobile-airport-group-items">
                {group.airports.map((airport) => (
                  (() => {
                    const unreachable = planningMode && airport.iata !== routeOriginId && !reachableAirportIds?.has(airport.iata);
                    const showDistance = Boolean(distanceReference && airport.iata !== distanceReferenceId);
                    const distance = showDistance ? Math.round(distanceKm(distanceReference!, airport)).toLocaleString() : '';
                    const routeCount = routeAirportCounts?.get(airport.iata) ?? 0;
                    return (
                      <button
                        type="button"
                        key={airport.iata}
                        data-airport-id={airport.iata}
                        className={`${selectedAirportId === airport.iata ? 'selected ' : ''}${marketClass(airport.market)}`}
                        disabled={unreachable}
                        title={unreachable ? t('ui.不可抵达') : undefined}
                        onClick={() => onAirportSelect(airport.iata)}
                      >
                        <span>
                          <strong className={hubAirportIds?.has(airport.iata) ? 'hub' : undefined}>
                            {airport.nameZh}
                            {routeCount > 0 && (
                              <span className="mobile-airport-route-count" aria-label={`${routeCount} 条航线`}>
                                ({routeCount})
                              </span>
                            )}
                            {planningMode && routeAirportIds?.has(airport.iata) && <span className="mobile-airport-route-check" aria-label="已有航线">✓</span>}
                          </strong>
                      <small>{airport.iata} · {airport.country}</small>
                      {showDistance && <small className="mobile-airport-distance">{t(planningMode ? 'ui.航程 {distance} km' : 'ui.距离 {distance} km', { distance })}</small>}
                    </span>
                    <b>{unreachable ? t('ui.不可抵达') : t('ui.市场 {market}', { market: airport.market })}</b>
                      </button>
                    );
                  })()
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </section>
  );
}
